import React, { useEffect, useState } from 'react';
import { ReactComponent as ArrowLeft } from 'icons/arrow-left.svg';
import { Button, InputField, RadioButton, TableWrapper } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'components/Context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkEmptyObject, checkPermission } from 'utils/functionality';
import { getAllRentalLocation } from 'features/rental-location/actions';
import { ReactComponent as HolidayChargeIcon } from 'icons/holiday-charge-filled-icon.svg';
import { ReactComponent as Mappin } from 'icons/mappin-icon.svg';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { addNewHoliday, getAllEvents, getHolidayDetail, updateHolidayById } from 'features/holiday-charge/actions';
import { resetSelectedHoliday } from 'features/holiday-charge/slice';
import { trackEvent } from 'utils/analytics';

const arrayRadionButton = [
  {
    id: 'pr1',
    label: 'Harga Fixed',
    value: 'fixed',
  },
  {
    id: 'pr2',
    label: 'Harga Percentage',
    value: 'percentage',
  },
];

const chargeSettingData = [
  {
    id: 1,
    name: 'Charge per-Day',
    value: 'daily',
  },
  {
    id: 2,
    name: '1x Charge',
    value: 'once',
  },
];

const HolidayChargeDetail = () => {
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: rentalLocationData } = useSelector((state) => state.rentalLocation);
  const eventsData = useSelector((state) => state.events.data);
  const selectedHolidayData = useSelector((state) => state.holidayCharge.selected);

  const { showToast, setShowConfirmation } = useAppContext();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id: 0,
    value: 0,
    value_type: '',
  });
  const [mappedLocationList, setMappedLocationList] = useState([]);
  const [mappedEventList, setMappedEventList] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({
    id: 0,
    name: '',
  });
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [priceType, setPriceType] = useState('');
  const [selectedSetting, setSelectedSetting] = useState(0);

  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');

  const isAddState = location.pathname.includes('add');

  useEffect(() => {
    if (id) {
      dispatch(getHolidayDetail(id));
    }
  }, [id]);

  useEffect(() => {
    dispatch(getAllEvents({ page: 1, limit: 20 }));
    dispatch(getAllRentalLocation());
  }, []);

  useEffect(() => {
    if (checkEmptyObject(selectedHolidayData)) return;
    setFormData({
      id: selectedHolidayData?.id,
      value: selectedHolidayData?.charges,
      value_type: selectedHolidayData?.value_type,
    });
    setSelectedLocation({ id: selectedHolidayData?.location_id });
    setSelectedEvents(selectedHolidayData?.name);
    setPriceType(arrayRadionButton.find((item) => item.value === selectedHolidayData?.value_type)?.id);
    setSelectedSetting(chargeSettingData.find((item) => item.value === selectedHolidayData?.charge_mode)?.id);
  }, [selectedHolidayData]);

  useEffect(() => {
    if (!rentalLocationData?.length) return;
    const newSelectedLocationArr = rentalLocationData.map((item) => ({ ...item, value: item.name.toLowerCase() }));
    setMappedLocationList(newSelectedLocationArr);
  }, [rentalLocationData]);

  useEffect(() => {
    if (checkEmptyObject(eventsData)) return;
    const newEventsData = eventsData?.data?.map((item) => ({
      ...item,
      value: item.name.toLowerCase().concat(`-${item.id}`),
    }));
    setMappedEventList(newEventsData);
  }, [eventsData]);

  const backHandler = () => {
    navigate(-1);
  };

  const updateHandler = async () => {
    if (!selectedLocation.id) {
      showToast({ type: 'warning', message: 'Harap Memilih Lokasi Yang Tersedia' });
      return;
    }
    if (typeof selectedEvents === 'object' && !selectedEvents.length) {
      showToast({ type: 'warning', message: 'Harap Memilih Event Hari Libur' });
      return;
    }
    if (!selectedSetting) {
      showToast({ type: 'warning', message: 'Harap Memilih Charge Setting' });
      return;
    }
    if (formData.value_type.trim() === '') {
      showToast({ type: 'warning', message: 'Harap Memilih Tipe Value' });
      return;
    }

    const payload = {
      charges: formData.value,
      value_type: formData.value_type,
      charge_mode: chargeSettingData.find((item) => item.id === selectedSetting).value,
    };

    setShowConfirmation({
      message: isAddState ? 'Apakah Anda yakin ingin menyimpan data ini?' : 'Apakah Anda ingin mengubah data ini?',
      show: true,
      onClick: async () => {
        try {
          if (isAddState) {
            trackEvent('holiday_charge_created', { source: 'holiday_charge_detail' });
            await dispatch(
              addNewHoliday({
                ...payload,
                events_id: selectedEvents.map((item) => item.id),
                location_id: selectedLocation.id,
              }),
            ).unwrap();
            showToast({ type: 'success', message: 'Berhasil Menambahkan Data' });
          } else {
            await dispatch(updateHolidayById({ id: id, payload: payload })).unwrap();
            showToast({ type: 'success', message: 'Berhasil Mengubah Data' });
          }
          dispatch(resetSelectedHoliday());
        } catch (error) {
          showToast({ type: 'error', message: `Terjadi Kesalahan: ${error.slug}` });
        } finally {
          navigate('/holiday-charge');
        }
      },
    });
  };

  const handleLocation = (e) => {
    if (+e.target.id === selectedLocation.id) return;
    setSelectedEvents([]);
    const { id } = e.target;
    const existedType = rentalLocationData?.find((item) => item.id === +id);

    setSelectedLocation({ id: existedType.id, name: existedType.name, value: existedType.name.toLowerCase() });
    dispatch(getAllEvents({ limit: 20, locationId: existedType.id }));
  };

  const handleEvents = (id, status) => {
    if (isAddState) {
      const existedType = selectedEvents?.find((item) => item.id === id);

      // if exist will return nothing
      if (existedType && status) return;
      const eventValue = mappedEventList?.find((el) => el.id == id);

      if (status) {
        setSelectedEvents(() => [
          ...selectedEvents,
          {
            id: eventValue.id,
            name: eventValue.name,
            value: eventValue.name.toLowerCase().concat(`-${eventValue.id}`),
          },
        ]);
      } else {
        setSelectedEvents((prev) => prev.filter((item) => item.id !== eventValue.id));
      }
    } else {
      setSelectedEvents(+id.target.id);
    }
  };

  const handleValueType = (id) => {
    setPriceType(id);
    const ValueType = arrayRadionButton.find((item) => item.id == id)?.value;

    setFormData({ ...formData, value_type: ValueType });
  };

  const handleValueInput = (e) => {
    setFormData({ ...formData, value: isNaN(e.target.value) ? 0 : +e.target.value });
  };

  const numberInputOnWheelPreventChange = (e) => {
    // Prevent the input value change
    e.target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current
    // function is done)
    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  return (
    <div className="holiday-charge">
      <div className="holiday-charge-detail">
        <button className="holiday-charge-detail__back-btn" onClick={backHandler}>
          <ArrowLeft />
          <span>Kembali</span>
        </button>

        <TableWrapper title="Holiday Charge" icon={<HolidayChargeIcon fill="#FF9900" width="25px" height="25px" />}>
          <h3 className="holiday-charge-detail__title">Set Holiday Charge</h3>
          <div className="holiday-charge-detail__content">
            <SelectFieldDropdown
              label="Lokasi"
              placeholder="Pilih Lokasi"
              data={mappedLocationList}
              value={selectedLocation.id}
              onChange={handleLocation}
              htmlFor="location"
              icon={<Mappin className="select-field-shuttle__input-field__arrow" />}
              disable={!isAddState}
            />
          </div>
          <div className="holiday-charge-detail__content">
            {isAddState ? (
              <SelectFieldDropdown
                label="Nama Hari Libur"
                placeholder="Pilih Hari Libur"
                data={mappedEventList}
                value={selectedEvents}
                onChange={handleEvents}
                htmlFor="holiday-name"
                checkBox={isAddState}
                disable={!selectedLocation.id || !isAddState}
              />
            ) : (
              <InputField
                label="Nama Hari Libur"
                placeholder="Pilih Hari Libur"
                value={selectedEvents}
                htmlFor="holiday-name"
                disable
              />
            )}
          </div>
          <div className="holiday-charge-detail__content">
            <SelectFieldDropdown
              label="Charge Setting"
              placeholder="Pilih Charge Setting"
              data={chargeSettingData}
              value={selectedSetting}
              onChange={(e) => setSelectedSetting(+e.target.id)}
              htmlFor="charge-setting"
            />
          </div>
          <div className="holiday-charge-detail__content">
            <div className="form-field__price">
              <h3>Charge Tambahan (Jika Ada)</h3>
              <div className="form-field__price__radio">
                <RadioButton name="price" data={arrayRadionButton} state={priceType} setState={handleValueType} />
              </div>
              <InputField
                type="number"
                placeholder={priceType === 'pr1' ? '0' : priceType === 'pr2' ? '0%' : '0'}
                value={formData.value || ''}
                onChange={handleValueInput}
                onWheel={numberInputOnWheelPreventChange}
                disable={!priceType}
              />
            </div>
          </div>

          <div className="holiday-charge-detail__button">
            <Button className="button" size="sm" width="192px" onClick={backHandler} variant="outline">
              Kembali
            </Button>
            {(isAddState || checkPermission(offCanvasMenu, currentMenu, 'update')) && (
              <Button className="button" size="sm" width="192px" onClick={updateHandler}>
                Simpan
              </Button>
            )}
          </div>
        </TableWrapper>
      </div>
    </div>
  );
};

export default HolidayChargeDetail;
